// Tool Scout API — read a connected app (read-only, via Composio) and get
// grounded delegation proposals. Backed by aibot's /dashboard/tools routes.

import axios from 'axios';
import { getApiBaseUrl } from './api';

export interface ScoutObservation {
  stat: string;
  label: string;
  detail: string;
}

export interface ScoutSkillDraft {
  name: string;
  description: string;
  instructions: string;
}

/** A user-adjustable setting the scout GENERATED from the data it read —
 *  options are their actual labels/channels/senders, not placeholders. */
export interface ScoutSetting {
  key: string;
  label: string;
  type: 'select' | 'multi' | 'time' | 'toggle' | 'text';
  options: string[];
  value: string | boolean | string[];
  hint: string;
}

export type ScoutKind = 'skill' | 'routine' | 'workflow' | 'plugin' | 'app_idea';

export interface ScoutProposal {
  id: string;
  kind: ScoutKind;
  title: string;
  tagline: string;
  /** Cites the actual data the scout read — the quality gate. */
  evidence: string;
  /** What it will actually do, in order (all kinds except app_idea). */
  steps: string[];
  /** Pre-accept configuration (skill / routine / workflow only). */
  settings: ScoutSetting[];
  never_without_asking: string[];
  skill_draft: ScoutSkillDraft | null;
  /** Plugin pack — 2-4 related skills. */
  skills: ScoutSkillDraft[] | null;
  scoping_prompt: string | null;
}

export interface ScoutReport {
  ok: true;
  provider: string;
  /** ISO timestamp of when this report was generated (reports are stored server-side). */
  generated_at?: string;
  read_only: true;
  probes: Array<{ label: string; ok: boolean }>;
  headline: string;
  observations: ScoutObservation[];
  proposals: ScoutProposal[];
}

export interface ScoutFailure {
  ok: false;
  error: 'unsupported_provider' | 'read_failed' | 'llm_not_configured' | 'synthesis_failed' | 'no_grounded_proposals' | string;
  detail?: string;
  supported?: string[];
}

export type ScoutResult = ScoutReport | ScoutFailure;

function base(clientId: string): string {
  return `${getApiBaseUrl()}/dashboard/tools/${encodeURIComponent(clientId)}/scout`;
}

export async function fetchScoutPlan(
  clientId: string,
  provider: string,
): Promise<{ supported: boolean; steps: string[] }> {
  const res = await axios.get(`${base(clientId)}/plan`, { params: { provider } });
  return { supported: !!res.data?.supported, steps: res.data?.steps || [] };
}

export async function fetchSavedReport(
  clientId: string,
  provider: string,
): Promise<ScoutReport | null> {
  const res = await axios.get(`${base(clientId)}/report`, { params: { provider } });
  return res.data?.found && res.data?.report?.ok ? (res.data.report as ScoutReport) : null;
}

export async function runScout(
  clientId: string,
  provider: string,
  clientName?: string | null,
): Promise<ScoutResult> {
  const res = await axios.post(base(clientId), {
    provider,
    client_name: clientName || undefined,
  });
  return res.data as ScoutResult;
}
